// ═══════════════════════════════════════════════════════════════════
// artwork-viewer.js  v2.0
// Usage:
//   ArtworkViewer.init('#my-container', { ...options });
//
// Options (all optional — defaults shown):
//   images: {
//     cover:         'Infinity-cover-sq.jpg',
//     back:          'Infinity-back-sq.jpg',
//     gatefoldLeft:  'gatefold-left.jpg',
//     gatefoldRight: 'gatefold-right.jpg',
//     vinyl:         'vinyl-1.png',
//     vinyl2:        'vinyl-2.png',   // right vinyl in dual-vinyl modes
//     vinyl3:        '',              // expand-tilt: optional inner left vinyl (sits on top of the left one)
//     vinyl4:        '',              // expand-tilt: optional inner right vinyl (sits on top of the right one)
//   },
//   modes: ['flip-tilt','front-on','gatefold-open','gatefold-back','gatefold-vinyl','expand-tilt'],
//   ui:      { showModeBar: true, showSliders: false,
//              loaderColor: '#fff',      // spinner colour while images load
//              loaderTimeout: 10000,     // ms — reveal artwork anyway if images are slow
//              textColor: '#fff',        // promo text + button colour
//              unstyledText: false,      // true = drop the viewer's promo text styling so theme styles apply
//                                        // (or set unstyledText on topPromo / bottomPromo individually)
//              stackButtonsBelow: 600,   // px — promo buttons stack full-width below this width
//              topPromo: { text: '...', links: [...],   // link: { label, href, newWindow, class: 'replaces built-in button class' }
//                          fade: { in: 'start'|'intro'|'after-intro'|0.2|[0.1,0.3],
//                                  out: 'end'|'fade-end'|0.8|[0.8,1] } },
//              bottomPromo: { text: '...', links: [...],
//                             fade: { in: 'start'|'after-intro', out: 'end'|'fade-end' } } },
//   background: { start: '#000', afterIntro: '#111', end: '#fff' },  // all default '#000'; all-black = no override
//   intro:   { enabled: false, scrollPct: 0.60, size: 'cover',  // size: 'cover' | 'contain' | 'height' | px number
//              tiltStart: null },  // expand-tilt: 0–1 point in the intro where the tilt starts (null = tilt after intro)
//   panel:   { maxPx: 360, vw: 0.42 },
//   expand:  { peek: 0.20 },   // fraction of panel width visible beyond cover edge
//   pointer: { enabled: true, rx: 8, ry: 18, rz: 3, ease: 0.04 },
//   camera:  { enabled: false,      // expand-tilt: direct the view like a camera
//              push: 0.06,          // slow push-in as the set assembles (fraction of scale)
//              drift: true,         // barely-there idle drift once tilted, so the frame never freezes
//              focus: 0,            // rack-focus blur in px for out-of-focus pieces (0 = off)
//              vignette: 0.3 },     // 0–1 spotlight / edge falloff on the background
//   motion:  { enabled: false,      // expand-tilt: physical motion — lift, slide, settle
//              overshoot: 0,        // how far pieces run past their mark before settling (0 = none, 0.25 ≈ 4%)
//              stagger: 0.04,       // scroll offset between paired pieces
//              lift: 0.06,          // cover lift as it slides off the back (units of panel size)
//              spin: 35,            // degrees each vinyl turns as it slides out
//              contact: true },     // tight shadows under gatefolds and vinyls
//   tilt:    { perspective: 4.4 },  // expand-tilt camera distance in panel widths (lower = stronger perspective)
//   light:   { enabled: false,      // expand-tilt: one key light across the whole set
//              x: 0.05, y: -0.55,   // light position on the tilted table (units of panel size)
//              radius: 1.8,         // highlight falloff (units of panel size)
//              intensity: 0.6,      // 0–1 strength of sleeve light / vinyl sheen
//              glint: true,         // light sweeps across the cover as it tilts (scroll-driven)
//              glintStrength: 0.4,  // 0–1 peak brightness of the glint
//              sheen: true },       // light streak on the vinyl grooves
// ═══════════════════════════════════════════════════════════════════

const ArtworkViewer = (() => {

  // ── Pure math helpers (shared, stateless) ─────────────────────
  const clamp01  = x       => Math.max(0, Math.min(1, x));
  const lerp     = (a,b,t) => a + (b-a)*t;
  const sStep    = t       => t*t*(3-2*t);
  const invlerp  = (a,b,v) => clamp01((v-a)/(b-a));
  const phaseT   = (p,a,b) => sStep(invlerp(a,b,p));
  // Smoothstep plus a forward push that runs past the mark near the end, then settles back.
  // o = 0.25 gives ~5% overshoot at ~87% of the move; still exactly 0 → 1.
  const settle   = (t,o)   => sStep(t) + o*t*Math.sin(Math.PI*t);
  const shadow   = alpha   => alpha <= 0 ? 'none'
    : `-9px 14px 29px rgba(0,0,0,${(alpha*.53).toFixed(2)}),-5px 6px 10px rgba(0,0,0,${(alpha*.25).toFixed(2)})`;
  // Same shadow, cast along a unit direction (dx, dy) in the element's own plane
  // k > 1 = lifted further off the table: longer, softer, lighter shadow
  const shadowDir = (alpha, dx, dy, k = 1) => alpha <= 0 ? 'none'
    : `${(dx*16.6*k).toFixed(1)}px ${(dy*16.6*k).toFixed(1)}px ${(29*k).toFixed(0)}px rgba(0,0,0,${(alpha*.53/Math.sqrt(k)).toFixed(2)}),`+
      `${(dx*7.8*k).toFixed(1)}px ${(dy*7.8*k).toFixed(1)}px ${(10*k).toFixed(0)}px rgba(0,0,0,${(alpha*.25/Math.sqrt(k)).toFixed(2)})`;

  // ── Colour helpers ───────────────────────────────────────────
  const hexToRgb = hex => {
    const h = hex.replace('#','');
    const s = h.length === 3 ? h.split('').map(c=>c+c).join('') : h;
    return [parseInt(s.slice(0,2),16), parseInt(s.slice(2,4),16), parseInt(s.slice(4,6),16)];
  };
  const lerpColor = (a,b,t) =>
    `rgb(${Math.round(lerp(a[0],b[0],t))},${Math.round(lerp(a[1],b[1],t))},${Math.round(lerp(a[2],b[2],t))})`;
  const isBlack   = ([r,g,b]) => r===0 && g===0 && b===0;

  // ── DOM helpers ───────────────────────────────────────────────
  const mk = (tag, cls, attrs={}) => {
    const e = document.createElement(tag);
    if (cls) e.className = cls;
    Object.entries(attrs).forEach(([k,v]) => e.setAttribute(k,v));
    return e;
  };
  const mkImg = (src, cls='', alt='') => mk('img', cls, {src, alt});

  // ── Default config ────────────────────────────────────────────
  const DEFAULTS = {
    images: {
      cover:         'Infinity-cover-sq.jpg',
      back:          'Infinity-back-sq.jpg',
      gatefoldLeft:  'gatefold-left.jpg',
      gatefoldRight: 'gatefold-right.jpg',
      vinyl:         'vinyl-1.png',
      vinyl2:        'vinyl-2.png',
    },
    modes: ['flip-tilt','front-on','gatefold-open','gatefold-back','gatefold-vinyl','expand-tilt','expand'],

    ui: { showModeBar: true, showSliders: false, clipOverflow: true, topPromo: null, bottomPromo: null,
          loaderColor: '#fff', loaderTimeout: 10000,
          textColor: '#fff',          // promo text + button colour
          unstyledText: false,        // true = no viewer text styling on promo text (theme styles apply)
          stackButtonsBelow: 600 },   // px — promo buttons stack full-width below this viewport width

    intro:   { enabled: false, scrollPct: 0.60, size: 'cover', tiltStart: null },
    panel:   { maxPx: 360, vw: 0.42 },
    expand:  { peek: 0.20 },
    pointer: { enabled: true, rx: 8, ry: 18, rz: 3, ease: 0.04 },
    tilt:    { perspective: 4.4 },
    camera:  { enabled: false, push: 0.06, drift: true, focus: 0, vignette: 0.3 },
    motion:  { enabled: false, overshoot: 0, stagger: 0.04, lift: 0.06, spin: 35, contact: true },
    light:   { enabled: false, x: 0.05, y: -0.55, radius: 1.8, intensity: 0.6, glint: true, glintStrength: 0.4, sheen: true },
  };

  // ── All available mode definitions ───────────────────────────
  const MODE_DEFS = {
    'flip-tilt':      { label: 'Flip Tilt',       introScale: 0.72 },
    'front-on':       { label: 'Front-on Flip',    introScale: 1    },
    'gatefold-open':  { label: 'Gatefold Open',    introScale: 1    },
    'gatefold-back':  { label: 'Gatefold + Back',  introScale: 1    },
    'gatefold-vinyl': { label: 'Gatefold + Vinyl', introScale: 1    },
    'expand':         { label: 'Expand',           introScale: 1    },
    'expand-tilt':    { label: 'Expand & Tilt',    introScale: 0.72 },
  };

  // ── Tilt target angles (shared constants) ────────────────────
  const T_RX = 36, T_RY = 4, T_RZ = -32;

  // ── Expand & Tilt layout, in table coordinates (units of panel size P) ──
  // The stack is a tilted "table" carrying the tilt above; each layer slides across
  // it in the table's own x/y plane, so everything shares one angle and one perspective.
  const ET = {
    split: [0.193, 0.107], // cover → +split, back → −split (reads as a horizontal split on screen)
    nudge: [0.1, -0.036],  // extra shift for the cover group (cover, right gatefold, right vinyl) — a touch up and right on screen
    nudgeBack: [0.043, -0.068], // extra shift for the back group (back, left gatefold, left vinyls) — straight up on screen
    vinyl: { left: 0.675, right: 0.8 }, // outer vinyls slide out this far along the sleeve's x edge
    vinylInner: { left: 0.5, right: 0.59 }, // inner vinyls (vinyl3 / vinyl4) stop this fraction of the outer ones' travel
    gfR:   0.436,          // right gatefold slides out from under the cover along +y
    gfL:   0.243,          // left gatefold slides out from under the back along −y
    zStep: 1,              // px between layers, so 3D depth sorting keeps the DOM stacking order
  };

  // ── Instance counter for unique IDs ──────────────────────────
  let _uid = 0;

  // ═══════════════════════════════════════════════════════════════
  // Instance class
  // ═══════════════════════════════════════════════════════════════
  class Instance {
    constructor(container, userConfig) {
      this.uid = 'gv' + (++_uid);
      this.container = container;

      // Deep-merge config
      this.cfg = {
        images:  { ...DEFAULTS.images,  ...(userConfig.images  || {}) },
        modes:   userConfig.modes   || [...DEFAULTS.modes],
        ui:      { ...DEFAULTS.ui,      ...(userConfig.ui      || {}) },
        intro:   { ...DEFAULTS.intro,   ...(userConfig.intro   || {}) },
        panel:   { ...DEFAULTS.panel,   ...(userConfig.panel   || {}) },
        expand:     { ...DEFAULTS.expand,  ...(userConfig.expand  || {}) },
        pointer:    { ...DEFAULTS.pointer, ...(userConfig.pointer || {}) },
        light:      { ...DEFAULTS.light,   ...(userConfig.light   || {}) },
        tilt:       { ...DEFAULTS.tilt,    ...(userConfig.tilt    || {}) },
        motion:     { ...DEFAULTS.motion,  ...(userConfig.motion  || {}) },
        camera:     { ...DEFAULTS.camera,  ...(userConfig.camera  || {}) },
        background: userConfig.background || null,
      };

      this.maxPx       = this.cfg.panel.maxPx;
      this.vw          = this.cfg.panel.vw;
      this.currentMode = 0;
      this._introP     = 1;
      this.ptr         = { tx:0, ty:0, x:0, y:0 };
      this._stableW    = window.innerWidth;
      this._stableVH   = window.innerHeight;

      this._buildCSS();
      this._buildDOM();
      this._bindEvents();
      this._applyGatefoldSizing();
      this._switchMode(0);
      if (this.cfg.pointer.enabled) requestAnimationFrame(() => this._pointerLoop());
    }

    // ── Sizing helpers ────────────────────────────────────────
    _panelSize() {
      const vw = document.documentElement.clientWidth || window.innerWidth;
      return Math.min(vw * this.vw, this.maxPx);
    }

    // ── CSS injection (scoped to uid) ─────────────────────────
    _buildCSS() {
      const u = this.uid;
      if (document.getElementById(`${u}-styles`)) return;
      const s = document.createElement('style');
      s.id = `${u}-styles`;
      s.textContent = `
        .${u}-root { position:relative; }
        .${u}-section { background:transparent; }
        .${u}-stage {
          position:sticky; top:0; height:100dvh; height:100svh;
          display:grid; place-items:center; overflow:visible;
          transition: height 0.25s ease-out;
        }
        .${u}-anim { display:none; transform-style:preserve-3d; transition:opacity .4s ease; }
        /* Loader — artwork stays hidden until every image has loaded */
        .${u}-loading .${u}-anim { opacity:0; }
        .${u}-loader {
          position:absolute; inset:0; display:grid; place-items:center;
          pointer-events:none; z-index:50; transition:opacity .3s ease;
        }
        .${u}-stage:not(.${u}-loading) .${u}-loader { opacity:0; }
        .${u}-loader::after {
          content:''; width:36px; height:36px; border-radius:50%;
          border:2px solid currentColor; border-top-color:transparent;
          animation:${u}-spin .8s linear infinite;
        }
        @keyframes ${u}-spin { to { transform:rotate(360deg); } }
        /* Flip */
        .${u}-flip-scene { perspective:9999px; perspective-origin:50% 40%; }
        .${u}-flip-card {
          position:relative; transform-style:preserve-3d;
          transform-origin:center center; will-change:transform;
        }
        .${u}-flip-face {
          position:absolute; inset:0; width:100%; height:100%;
          object-fit:cover; backface-visibility:hidden;
          -webkit-backface-visibility:hidden; pointer-events:none;
        }
        .${u}-flip-back { transform:rotateY(180deg); }
        /* Gatefold */
        .${u}-gf-scene {
          perspective:900px; perspective-origin:50% 40%;
          transform-style:preserve-3d; transform-origin:center center;
          will-change:transform;
        }
        .${u}-gf-wrap { position:relative; transform-style:preserve-3d; will-change:transform; }
        .${u}-gf-panel {
          position:absolute; top:0; transform-origin:left center;
          transform-style:preserve-3d; will-change:transform;
        }
        .${u}-gf-r { z-index:1; } .${u}-gf-l { z-index:2; }
        .${u}-face {
          position:absolute; inset:0; width:100%; height:100%;
          object-fit:cover; display:block; pointer-events:none;
          backface-visibility:hidden; -webkit-backface-visibility:hidden;
        }
        .${u}-face-back { transform:rotateY(180deg); }
        .${u}-vinyl {
          position:absolute; left:0; transform-origin:left center;
          transform-style:preserve-3d; will-change:transform,opacity;
          pointer-events:none; opacity:0; z-index:-1;
        }
        .${u}-vinyl img {
          position:absolute; inset:0; width:100%; height:100%;
          object-fit:contain; display:block;
        }
        /* Expand */
        .${u}-ex-scene { overflow:visible; }
        .${u}-ex-stack { position:relative; transform-origin:center center; }
        .${u}-ex-layer {
          position:absolute; will-change:transform,opacity;
          transform-origin:center center;
        }
        .${u}-ex-layer img {
          position:absolute; inset:0; width:100%; height:100%;
          object-fit:cover; display:block; pointer-events:none;
        }
        .${u}-ex-vinyl img  { object-fit:contain; }
        .${u}-ex-gf, .${u}-ex-vinyl { opacity:0; }
        /* Expand & Tilt */
        .${u}-et-scene { perspective:9999px; perspective-origin:50% 40%; transform-origin:center center; }
        /* Camera: idle drift (amplitude follows --d), background vignette */
        .${u}-et-cam { --d:0; }
        .${u}-et-cam.${u}-drift { animation:${u}-drift 11s ease-in-out infinite alternate; }
        @keyframes ${u}-drift {
          0%   { transform:translate(0,0) rotate(0deg); }
          33%  { transform:translate(calc(var(--d)*-5px), calc(var(--d)*3px)) rotate(calc(var(--d)*.35deg)); }
          66%  { transform:translate(calc(var(--d)*3px),  calc(var(--d)*-4px)) rotate(calc(var(--d)*-.25deg)); }
          100% { transform:translate(calc(var(--d)*6px),  calc(var(--d)*2px)) rotate(calc(var(--d)*.15deg)); }
        }
        @media (prefers-reduced-motion: reduce) { .${u}-et-cam.${u}-drift { animation:none; } }
        .${u}-anim { position:relative; }   /* paint above the vignette */
        .${u}-vignette {
          position:absolute; inset:0; pointer-events:none; opacity:0;
          background:radial-gradient(ellipse 70% 60% at 47% 44%,
            rgba(255,255,255,.35), rgba(255,255,255,0) 55%, rgba(0,0,0,.45) 120%);
        }
        .${u}-et-stack {
          position:relative; transform-style:preserve-3d;
          transform-origin:center center; will-change:transform;
        }
        .${u}-et-layer {
          position:absolute; transform-origin:center center;
          will-change:transform,opacity;
        }
        .${u}-et-layer img {
          position:absolute; inset:0; width:100%; height:100%;
          object-fit:cover; display:block; pointer-events:none;
        }
        .${u}-et-vinyl img { object-fit:contain; }
        /* Expand & Tilt lighting overlays */
        .${u}-et-light, .${u}-et-shade, .${u}-et-glint, .${u}-et-sheen {
          position:absolute; inset:0; pointer-events:none; opacity:0;
        }
        .${u}-et-light { mix-blend-mode:screen; }    /* highlight nearest the light */
        .${u}-et-shade { mix-blend-mode:multiply; }  /* falloff on the far side */
        .${u}-et-glint {
          mix-blend-mode:screen;
          background:linear-gradient(115deg, transparent 36%, rgba(255,255,255,.75) 50%, transparent 64%);
          background-size:300% 100%; background-position:100% 0;
        }
        .${u}-et-sheen {
          mix-blend-mode:screen;
          /* two opposed streaks, as a single light catches the grooves */
          background:conic-gradient(rgba(255,255,255,0) 0deg, rgba(255,255,255,.45) 20deg, rgba(255,255,255,0) 45deg,
                                    rgba(255,255,255,0) 180deg, rgba(255,255,255,.45) 200deg, rgba(255,255,255,0) 225deg);
          -webkit-mask:radial-gradient(circle closest-side, transparent 34%, #000 36%, #000 97%, transparent 99%);
                  mask:radial-gradient(circle closest-side, transparent 34%, #000 36%, #000 97%, transparent 99%);
        }
        .${u}-et-vinyl, .${u}-et-back, .${u}-et-gfl, .${u}-et-gfr { opacity:0; }
        /* Bottom bar — stacks promo + mode bar */
        .${u}-bottom-bar {
          position:absolute; bottom:28px;
          width:90%; max-width:600px;
          display:flex; flex-direction:column; align-items:center; gap:12px;
          z-index:100; padding:0 16px;
        }
        /* Mode bar */
        .${u}-mode-bar {
          display:flex; gap:8px; flex-wrap:wrap; justify-content:center;
        }
        /* Promo */
        .${u}-promo {
          display:flex; flex-direction:column; align-items:center; gap:8px;
        }
        .${u}-promo-text {
          font-size:11px; letter-spacing:.14em; text-transform:uppercase;
          color:var(--${u}-ink); text-align:center; line-height:1.45;
          text-wrap:balance;
        }
        .${u}-promo-text > * { margin:0 0 .45em; font-size:1em; line-height:inherit; }
        .${u}-promo-text > :last-child { margin-bottom:0; }
        .${u}-promo-text :is(h1,h2,h3) { font-size:1.4em; }
        .${u}-promo-text-raw { text-align:center; }
        .${u}-promo-links {
          display:flex; gap:8px; flex-wrap:wrap; justify-content:center;
        }
        .${u}-promo-link {
          font-size:10px; letter-spacing:.12em; text-transform:uppercase;
          color:var(--${u}-ink); background:transparent; border:1px solid var(--${u}-ink);
          border-radius:20px; padding:6px 18px; cursor:pointer;
          text-decoration:none; display:inline-block;
          transition:opacity .2s;
        }
        .${u}-promo-link:hover { opacity:.6; }
        .${u}-mode-btn {

          font-size:10px; letter-spacing:.12em; text-transform:uppercase;
          color:#555; background:transparent; border:1px solid #333;
          border-radius:20px; padding:6px 14px; cursor:pointer;
          transition:color .2s,border-color .2s; white-space:nowrap;
        }
        .${u}-mode-btn:hover  { color:#aaa; border-color:#666; }
        .${u}-mode-btn.active { color:#fff; border-color:#fff; }
        /* Sliders */
        .${u}-slider-bar {
          position:absolute; top:20px; right:20px;
          display:flex; flex-direction:column; gap:10px; z-index:100;
        }
        .${u}-slider-row { display:flex; align-items:center; gap:8px; }
        .${u}-slider-row label {

          font-size:10px; letter-spacing:.1em; text-transform:uppercase;
          color:#555; width:60px; text-align:right;
        }
        .${u}-slider-row input[type=range] {
          -webkit-appearance:none; width:120px; height:2px;
          background:#333; border-radius:2px; outline:none; cursor:pointer;
        }
        .${u}-slider-row input[type=range]::-webkit-slider-thumb {
          -webkit-appearance:none; width:12px; height:12px;
          border-radius:50%; background:#fff; cursor:pointer;
        }
        .${u}-slider-val {

          font-size:10px; color:#555; width:36px;
        }
        /* Phones: bigger type, buttons stacked full-width with comfortable tap targets */
        @media (max-width:${this.cfg.ui.stackButtonsBelow}px) {
          .${u}-promo-text { font-size:12px; }
          .${u}-promo, .${u}-promo-links { width:100%; }
          .${u}-promo-links { flex-direction:column; align-items:center; gap:10px; }
          .${u}-promo-link {
            width:100%; box-sizing:border-box; text-align:center;
            font-size:11px; padding:15px 18px; line-height:1;
          }
          .${u}-bottom-bar { bottom:max(20px, env(safe-area-inset-bottom)); }
        }
        /* Top bar */
        .${u}-top-bar {
          position:absolute; top:28px; 
          width:90%; max-width:600px;
          display:flex; flex-direction:column; align-items:center; gap:12px;
          z-index:100; padding:0 16px;
        }
      `;
      document.head.appendChild(s);
    }

    // ── DOM build ─────────────────────────────────────────────
    _waitForImages(el) {
      const loaded = img => img.decode
        ? img.decode().catch(() => {})
        : new Promise(r => { if (img.complete) r(); else img.onload = img.onerror = r; });
      const all     = Promise.all([...el.querySelectorAll('img')].map(loaded));
      const timeout = new Promise(r => setTimeout(r, this.cfg.ui.loaderTimeout));
      return Promise.race([all, timeout]);
    }

    _buildDOM() {
      const u   = this.uid;
      const img = this.cfg.images;

      // Root wrapper
      const root = mk('div', `${u}-root`);
      root.style.setProperty(`--${u}-ink`, this.cfg.ui.textColor);

      // Scroll section + stage
      const section = mk('section', `${u}-section`);
      section.id    = `${u}-section`;
      const stage   = mk('div', `${u}-stage`);
      stage.id      = `${u}-stage`;
      if (this.cfg.ui.clipOverflow) stage.style.overflow = 'hidden';
      section.appendChild(stage);
      root.appendChild(section);

      // Camera vignette sits behind the artwork
      if (this.cfg.camera.enabled) stage.appendChild(mk('div', `${u}-vignette`, { id: `${u}-vignette` }));

      // Build active modes
      this._modes = this.cfg.modes.map((key, i) => {
        const def     = MODE_DEFS[key];
        const modeEl  = this._buildMode(key, i);
        stage.appendChild(modeEl);
        return { key, label: def.label, el: modeEl, update: this._updateFnFor(key) };
      });

      // Loader — shown until every artwork image has loaded (or failed / timed out)
      const loader = mk('div', `${u}-loader`);
      loader.style.color = this.cfg.ui.loaderColor;
      stage.appendChild(loader);
      stage.classList.add(`${u}-loading`);
      this._waitForImages(stage).then(() => stage.classList.remove(`${u}-loading`));

      // Top promo
      if (this.cfg.ui.topPromo) {
        stage.appendChild(this._buildPromoEl(this.cfg.ui.topPromo, `${u}-top-bar`, `${u}-top-promo`));
      }

      // Bottom bar (bottomPromo + mode bar)
      const bottomPromo = this.cfg.ui.bottomPromo;
      const showModeBar = this.cfg.ui.showModeBar !== false;
      if (bottomPromo || showModeBar) {
        const bottomBar = mk('div', `${u}-bottom-bar`);

        if (bottomPromo) {
          bottomBar.appendChild(this._buildPromoEl(bottomPromo, `${u}-promo`, `${u}-bottom-promo`));
        }

        if (showModeBar) {
          const bar = mk('div', `${u}-mode-bar`);
          this._modes.forEach((m, i) => {
            const btn = mk('button', `${u}-mode-btn`);
            btn.dataset.mode = i;
            btn.textContent  = m.label;
            btn.addEventListener('click', () => this._switchMode(i, true));
            bar.appendChild(btn);
          });
          bottomBar.appendChild(bar);
        }

        stage.appendChild(bottomBar);
        this._bottomBar = bottomBar;
      }

      // Slider bar
      if (this.cfg.ui.showSliders) {
        const mkSlider = (label, id, min, max, value, suffix, onChange) => {
          const row   = mk('div', `${u}-slider-row`);
          const lbl   = mk('label'); lbl.textContent = label;
          const input = mk('input', '', { type:'range', id:`${u}-${id}`, min, max, value });
          const val   = mk('span', `${u}-slider-val`, { id:`${u}-${id}-val` });
          val.textContent = value + suffix;
          input.addEventListener('input', () => { val.textContent = input.value + suffix; onChange(+input.value); });
          row.appendChild(lbl); row.appendChild(input); row.appendChild(val);
          return row;
        };
        const sBar = mk('div', `${u}-slider-bar`);
        sBar.appendChild(mkSlider('Max px', 'sl-max', 100, 600, this.cfg.panel.maxPx, 'px', v => {
          this.cfg.panel.maxPx = v; this.maxPx = v;
          this._applyGatefoldSizing(); this._update();
        }));
        sBar.appendChild(mkSlider('VW', 'sl-vw', 10, 90, Math.round(this.cfg.panel.vw * 100), 'vw', v => {
          this.cfg.panel.vw = v / 100; this.vw = v / 100;
          this._applyGatefoldSizing(); this._update();
        }));
        stage.appendChild(sBar);
      }


      this.container.appendChild(root);
      this._stage   = stage;
      this._section = section;
    }

    _buildPromoEl(cfg, outerClass, id) {
      const u  = this.uid;
      const el = mk('div', outerClass);
      if (id) el.id = id;
      if (cfg.text) {
        // Unstyled text keeps only centring; typography comes from the site's own CSS
        const raw = cfg.unstyledText ?? this.cfg.ui.unstyledText;
        const txt = mk('div', raw ? `${u}-promo-text-raw` : `${u}-promo-text`);
        txt.innerHTML = cfg.text;
        el.appendChild(txt);
      }
      const linkList = cfg.links || (cfg.link ? [cfg.link] : []);
      if (linkList.length) {
        const row = mk('div', `${u}-promo-links`);
        linkList.forEach(lnk => {
          if (!lnk.href) return;
          // A custom class (class or className) replaces the built-in button class
          const a = mk('a', lnk.class || lnk.className || `${u}-promo-link`, { href: lnk.href });
          if (lnk.newWindow) a.setAttribute('target', '_blank');
          a.setAttribute('rel', 'noopener noreferrer');
          a.textContent = lnk.label || lnk.href;
          row.appendChild(a);
        });
        el.appendChild(row);
      }
      return el;
    }

    _buildMode(key, i) {
      const u   = this.uid;
      const img = this.cfg.images;
      const mid = `${u}-mode-${i}`;   // unique mode wrapper id
      const wrap = mk('div', `${u}-anim`, { id: mid });

      if (key === 'flip-tilt' || key === 'front-on') {
        const pfx   = key === 'flip-tilt' ? 'ft' : 'fo';
        const scene = mk('div', `${u}-flip-scene`, { id: `${u}-${pfx}-scene` });
        const card  = mk('div', `${u}-flip-card`,  { id: `${u}-${pfx}-card`  });
        card.appendChild(mkImg(img.cover, `${u}-flip-face`));
        card.appendChild(mkImg(img.back,  `${u}-flip-face ${u}-flip-back`));
        scene.appendChild(card);
        wrap.appendChild(scene);

      } else if (key === 'expand') {
        const scene  = mk('div', `${u}-ex-scene`, { id: `${u}-ex-scene` });
        const stack  = mk('div', `${u}-ex-stack`, { id: `${u}-ex-stack` });
        // Stacking order (bottom → top): vinyl2 (optional), vinyl1, gatefold-left, cover
        if (img.vinyl2) {
          const v2 = mk('div', `${u}-ex-layer ${u}-ex-vinyl`, { id: `${u}-ex-vinyl2` });
          v2.appendChild(mkImg(img.vinyl2, '', 'Vinyl 2'));
          stack.appendChild(v2);
        }
        const vinyl = mk('div', `${u}-ex-layer ${u}-ex-vinyl`, { id: `${u}-ex-vinyl` });
        vinyl.appendChild(mkImg(img.vinyl, '', 'Vinyl'));
        stack.appendChild(vinyl);
        const gfL   = mk('div', `${u}-ex-layer ${u}-ex-gf`, { id: `${u}-ex-gf`    });
        const cover = mk('div', `${u}-ex-layer`,              { id: `${u}-ex-cover` });
        gfL.appendChild(mkImg(img.gatefoldLeft, '', 'Gatefold Left'));
        cover.appendChild(mkImg(img.cover,      '', 'Cover'));
        stack.appendChild(gfL);
        stack.appendChild(cover);
        scene.appendChild(stack);
        wrap.appendChild(scene);

      } else if (key === 'expand-tilt') {
        const scene  = mk('div', `${u}-et-scene`, { id: `${u}-et-scene` });
        const stack  = mk('div', `${u}-et-stack`, { id: `${u}-et-stack` });
        // z-order bottom→top: vl, vr, vl2, vr2 (inner vinyls, on top of the outer ones),
        // gfL (under back), back, gfR (under cover), cover
        const vl  = mk('div', `${u}-et-layer ${u}-et-vinyl`, { id: `${u}-et-vl` });
        const vr  = mk('div', `${u}-et-layer ${u}-et-vinyl`, { id: `${u}-et-vr` });
        vl.appendChild(mkImg(img.vinyl,               '', 'Vinyl L'));
        vr.appendChild(mkImg(img.vinyl2 || img.vinyl, '', 'Vinyl R'));
        stack.appendChild(vl);
        stack.appendChild(vr);
        const vinyls = [vl, vr];
        [['vl2', img.vinyl3, 'Vinyl L inner'], ['vr2', img.vinyl4, 'Vinyl R inner']].forEach(([id, src, alt]) => {
          if (!src) return;
          const v = mk('div', `${u}-et-layer ${u}-et-vinyl`, { id: `${u}-et-${id}` });
          v.appendChild(mkImg(src, '', alt));
          stack.appendChild(v);
          vinyls.push(v);
        });
        if (img.gatefoldLeft) {
          const etGfL = mk('div', `${u}-et-layer ${u}-et-gfl`, { id: `${u}-et-gfl` });
          etGfL.appendChild(mkImg(img.gatefoldLeft, '', 'Gatefold Left'));
          stack.appendChild(etGfL);
        }
        const bk = mk('div', `${u}-et-layer ${u}-et-back`, { id: `${u}-et-back` });
        bk.appendChild(mkImg(img.back, '', 'Back'));
        stack.appendChild(bk);
        if (img.gatefoldRight) {
          const etGfR = mk('div', `${u}-et-layer ${u}-et-gfr`, { id: `${u}-et-gfr` });
          etGfR.appendChild(mkImg(img.gatefoldRight, '', 'Gatefold Right'));
          stack.appendChild(etGfR);
        }
        const cvr = mk('div', `${u}-et-layer`, { id: `${u}-et-cover` });
        cvr.appendChild(mkImg(img.cover, '', 'Cover'));
        stack.appendChild(cvr);
        // Lighting overlays sit above each layer's image
        const light = this.cfg.light;
        if (light.enabled) {
          stack.querySelectorAll(`.${u}-et-layer:not(.${u}-et-vinyl)`)
            .forEach(l => { l.appendChild(mk('div', `${u}-et-shade`)); l.appendChild(mk('div', `${u}-et-light`)); });
          if (light.glint) cvr.appendChild(mk('div', `${u}-et-glint`));
          if (light.sheen) vinyls.forEach(v => v.appendChild(mk('div', `${u}-et-sheen`)));
        }
        scene.appendChild(stack);
        // Camera wrapper carries the idle drift, outside the perspective scene
        const cam = mk('div', `${u}-et-cam`, { id: `${u}-et-cam` });
        if (this.cfg.camera.enabled && this.cfg.camera.drift) cam.classList.add(`${u}-drift`);
        cam.appendChild(scene);
        wrap.appendChild(cam);

      } else {
        // All gatefold variants
        const hasBack  = key !== 'gatefold-open';
        const hasVinyl = key === 'gatefold-vinyl';
        const pfx      = { 'gatefold-open':'go','gatefold-back':'gb','gatefold-vinyl':'gv' }[key];
        const k        = `${u}-${pfx}`;

        const scene  = mk('div', `${u}-gf-scene`, { id: `${k}-scene` });
        const gfWrap = mk('div', `${u}-gf-wrap`,  { id: `${k}-wrap`  });

        const panR = mk('div', `${u}-gf-panel ${u}-gf-r`, { id: `${k}-pr` });
        panR.appendChild(mkImg(img.gatefoldRight, `${u}-face`, 'GF Right'));
        if (hasBack)  panR.appendChild(mkImg(img.back, `${u}-face ${u}-face-back`, 'Back'));
        if (hasVinyl) { const v = mk('div', `${u}-vinyl`, {id:`${k}-vr`}); v.appendChild(mkImg(img.vinyl2)); panR.appendChild(v); }
        
        const panL = mk('div', `${u}-gf-panel ${u}-gf-l`, { id: `${k}-pl` });
        panL.appendChild(mkImg(img.cover, `${u}-face`, 'Cover'));
        panL.appendChild(mkImg(img.gatefoldLeft, `${u}-face ${u}-face-back`, 'GF Left'));
        if (hasVinyl) { const v = mk('div', `${u}-vinyl`, {id:`${k}-vl`}); v.appendChild(mkImg(img.vinyl)); panL.appendChild(v); }

        gfWrap.appendChild(panR);
        gfWrap.appendChild(panL);
        scene.appendChild(gfWrap);
        wrap.appendChild(scene);
      }
      return wrap;
    }

    _updateFnFor(key) {
      const map = {
        'flip-tilt':      p => this._updateFlipTilt(p),
        'front-on':       p => this._updateFrontOn(p),
        'gatefold-open':  p => this._updateGFOpen(p),
        'gatefold-back':  p => this._updateGFBack(p),
        'gatefold-vinyl': p => this._updateGFVinyl(p),
        'expand':         p => this._updateExpand(p),
        'expand-tilt':    p => this._updateExpandTilt(p),
      };
      return map[key] || (() => {});
    }

    // ── Sizing ────────────────────────────────────────────────
    _applyGatefoldSizing() {
      const u = this.uid;
      const P = this._panelSize();
      const W = P * 2;

      ['go','gb','gv'].forEach(pfx => {
        const k      = `${u}-${pfx}`;
        const wrapEl = document.getElementById(`${k}-wrap`);
        const panL   = document.getElementById(`${k}-pl`);
        const panR   = document.getElementById(`${k}-pr`);
        if (!wrapEl) return;

        wrapEl.style.cssText += `width:${W}px;height:${P}px;left:${-P}px;`;
        const sceneEl = wrapEl.parentElement;
        if (sceneEl) { sceneEl.style.width=P+'px'; sceneEl.style.height=P+'px'; sceneEl.style.overflow='visible'; }

        [panL,panR].forEach(e => { if (!e) return; e.style.width=P+'px'; e.style.height=P+'px'; e.style.left=P+'px'; });

        if (pfx === 'gv') {
          [`${k}-vl`,`${k}-vr`].forEach(id => {
            const ve = document.getElementById(id);
            if (!ve) return;
            const vs = P * 0.94;
            ve.style.width=vs+'px'; ve.style.height=vs+'px'; ve.style.top=((P-vs)/2)+'px';
          });
        }
      });

      // Flip cards
      ['ft','fo'].forEach(pfx => {
        const e = document.getElementById(`${u}-${pfx}-card`);
        if (!e) return;
        e.style.width=P+'px'; e.style.height=P+'px';
      });

      // Expand stack
      const exStack = document.getElementById(`${u}-ex-stack`);
      if (exStack) {
        exStack.style.width=P+'px'; exStack.style.height=P+'px';
        const GF = P * 0.98;
        const gfEl = document.getElementById(`${u}-ex-gf`);
        if (gfEl) { gfEl.style.width=GF+'px'; gfEl.style.height=GF+'px'; gfEl.style.top=((P-GF)/2)+'px'; gfEl.style.left='0'; }
        const vinylEl = document.getElementById(`${u}-ex-vinyl`);
        if (vinylEl) { vinylEl.style.width=GF+'px'; vinylEl.style.height=GF+'px'; vinylEl.style.top=((P-GF)/2)+'px'; }
        const vinyl2El = document.getElementById(`${u}-ex-vinyl2`);
        if (vinyl2El) { vinyl2El.style.width=GF+'px'; vinyl2El.style.height=GF+'px'; vinyl2El.style.top=((P-GF)/2)+'px'; }
        const coverEl = document.getElementById(`${u}-ex-cover`);
        if (coverEl) { coverEl.style.width=P+'px'; coverEl.style.height=P+'px'; }
      }

      // Expand & Tilt stack
      const stack = document.getElementById(`${u}-et-stack`);
      if (stack) {
        stack.style.width=P+'px'; stack.style.height=P+'px';
        stack.querySelectorAll(`.${u}-et-layer`).forEach(l => { l.style.width=P+'px'; l.style.height=P+'px'; });
      }

      // Lock both section and stage to px using stable vh — prevents jumps when mobile chrome toggles
      const introVh = this.cfg.intro.enabled ? this.cfg.intro.scrollPct : 0;
      this._section.style.height = ((500 + introVh * 100) * this._stableVH / 100) + 'px';
      this._stage.style.height   = this._stableVH + 'px';
    }

    // ── Scroll progress ───────────────────────────────────────
    _scrollP() {
      const rect     = this._section.getBoundingClientRect();
      const vh       = this._stableVH || window.innerHeight || 1;
      const total    = rect.height - vh;
      const traveled = -rect.top;

      if (this.cfg.intro.enabled) {
        const introH = vh * this.cfg.intro.scrollPct;
        this._introP = clamp01(traveled / introH);
        const mainTraveled = Math.max(0, traveled - introH);
        const mainTotal    = total - introH;
        return mainTotal > 0 ? clamp01(mainTraveled / mainTotal) : 0;
      }

      return total > 0 ? clamp01(traveled / total) : 0;
    }

    // ── Mode switching ────────────────────────────────────────
    // scrollToTop: jump to the top of the viewer so the new mode's animation starts
    // from the beginning — only for mode-button clicks, never on page load
    _switchMode(n, scrollToTop = false) {
      const u = this.uid;
      this._modes.forEach((m, i) => {
        m.el.style.display   = i === n ? 'block' : 'none';
        m.el.style.transform = '';
      });
      this.container.querySelectorAll(`.${u}-mode-btn`).forEach(b =>
        b.classList.toggle('active', parseInt(b.dataset.mode) === n));
      if (scrollToTop) window.scrollTo({ top: this._section.getBoundingClientRect().top + window.scrollY, behavior: 'instant' });
      this.currentMode = n;
      this._introP = this.cfg.intro.enabled ? 0 : 1;
      this._applyGatefoldSizing();
      this._update();
    }

    // ── Main update ───────────────────────────────────────────
    _update() {
      const p = this._scrollP();
      if (this.cfg.intro.enabled) this._updateIntro(this._introP);
      this._modes[this.currentMode].update(p);
      this._applyPromoFade(p);
      this._updateBackground(p);
      // Keep stage height in sync with actual visible area so bottom bar tracks screen bottom.
      // Section height (scroll math) stays locked to _stableVH — no jump.
      this._stage.style.height = window.innerHeight + 'px';
    }

    // Resolve a fade range value → 0..1 progress
    // Accepts: 'start' | 'end' | 'intro' | 'after-intro' | 'fade-end' | number (0-1) | [start, end]
    // 'start' / 'end' = no animation (always fully visible at that boundary)
    _resolveRange(range, p) {
      if (range === 'start')       return 1;
      if (range === 'end')         return 1;
      if (range === 'intro')       return sStep(clamp01(this._introP));
      if (range === 'after-intro') return sStep(invlerp(0,    0.08, p));
      if (range === 'fade-end')    return sStep(invlerp(0.85, 1.0,  p));
      if (typeof range === 'number') return sStep(invlerp(range, range + 0.08, p));
      return sStep(invlerp(range[0], range[1], p));
    }

    _applyPromoFade(p) {
      const u = this.uid;
      [[`${u}-top-promo`, this.cfg.ui.topPromo], [`${u}-bottom-promo`, this.cfg.ui.bottomPromo]]
        .forEach(([id, cfg]) => {
          if (!cfg?.fade) return;
          const el = document.getElementById(id);
          if (!el) return;
          let opacity = 1;
          if (cfg.fade.in  != null) opacity = Math.min(opacity,       this._resolveRange(cfg.fade.in,  p));
          if (cfg.fade.out != null) opacity = Math.min(opacity, 1 - this._resolveRange(cfg.fade.out, p));
          el.style.opacity       = clamp01(opacity).toFixed(3);
          el.style.pointerEvents = opacity < 0.05 ? 'none' : '';
        });
    }

    _updateBackground(p) {
      const bg = this.cfg.background;
      if (!bg) return;
      const c0 = hexToRgb(bg.start      || '#000');
      const c1 = hexToRgb(bg.afterIntro || '#000');
      const c2 = hexToRgb(bg.end        || '#000');
      if (isBlack(c0) && isBlack(c1) && isBlack(c2)) return;
      const color = (this.cfg.intro.enabled && this._introP < 1)
        ? lerpColor(c0, c1, sStep(this._introP))
        : lerpColor(c1, c2, sStep(p));
      this._section.style.background = color;
    }

    _updateIntro(t) {
      const P          = this._panelSize();
      const mode       = this._modes[this.currentMode];
      const innerScale = MODE_DEFS[mode.key].introScale || 1;
      const size       = this.cfg.intro.size;
      const vw         = document.documentElement.clientWidth || window.innerWidth;
      const vh         = this._stableVH || window.innerHeight;
      const target     = typeof size === 'number'
        ? size
        : size === 'height'
          ? vh
          : size === 'contain'
            ? Math.min(vw, vh)
            : Math.max(vw, vh); // 'cover'
      const sf = target / (P * innerScale);
      mode.el.style.transform = t >= 1 ? '' : `scale(${lerp(sf, 1, sStep(t)).toFixed(4)})`;
    }

    // ── Pointer helpers ───────────────────────────────────────
    get _ptrRX() { return this.cfg.pointer.enabled ? this.ptr.y * -this.cfg.pointer.rx : 0; }
    get _ptrRY() { return this.cfg.pointer.enabled ? this.ptr.x *  this.cfg.pointer.ry : 0; }
    get _ptrRZ() { return this.cfg.pointer.enabled ? this.ptr.x *  this.cfg.pointer.rz : 0; }
    _gfNudge()   { return `rotateX(${(this._ptrRX * 0.8).toFixed(2)}deg) rotateY(${(this._ptrRY * 0.8).toFixed(2)}deg)`; }

    // ── Animation functions ───────────────────────────────────
    _updateFlipTilt(p) {
      const u  = this.uid;
      const P  = this._panelSize(), BP = P * 4.4;
      const sc = document.getElementById(`${u}-ft-scene`);
      const cd = document.getElementById(`${u}-ft-card`);
      const t1 = phaseT(p, 0.0, 0.40), tF = phaseT(p, 0.50, 0.90);
      sc.style.perspective = lerp(lerp(BP*2,BP,t1),BP*.6,Math.sin(tF*Math.PI)).toFixed(0)+'px';
      cd.style.transform =
        `scale(${lerp(.72,1,phaseT(p,0,.45)).toFixed(4)}) `+
        `rotateX(${(lerp(0,T_RX,t1)+this._ptrRX).toFixed(2)}deg) `+
        `rotateZ(${(lerp(0,T_RZ,t1)+this._ptrRZ).toFixed(2)}deg) `+
        `rotateY(${(lerp(0,T_RY,t1)+this._ptrRY+lerp(0,-180,tF)).toFixed(2)}deg)`;
      cd.style.boxShadow = shadow(t1);
    }

    _updateFrontOn(p) {
      const u  = this.uid;
      const P  = this._panelSize(), BP = P * 4.4;
      const sc = document.getElementById(`${u}-fo-scene`);
      const cd = document.getElementById(`${u}-fo-card`);
      const tF = phaseT(p, 0.10, 0.90);
      sc.style.perspective = lerp(BP,BP*.6,Math.sin(tF*Math.PI)).toFixed(0)+'px';
      cd.style.transform = `rotateX(${this._ptrRX.toFixed(2)}deg) rotateY(${(lerp(0,-180,tF)+this._ptrRY).toFixed(2)}deg)`;
    }

    _updateGFOpen(p) {
      const u   = this.uid, k = `${u}-go`;
      const P   = this._panelSize(), BP = P * 2.5;
      const sc  = document.getElementById(`${k}-scene`);
      const wr  = document.getElementById(`${k}-wrap`);
      const tO  = phaseT(p, 0.20, 0.90);
      wr.style.left = lerp(-P,-P/2,tO).toFixed(1)+'px';
      document.getElementById(`${k}-pl`).style.transform = `rotateY(${lerp(0,-166,tO).toFixed(2)}deg)`;
      document.getElementById(`${k}-pr`).style.transform = `rotateY(${lerp(0,-14,tO).toFixed(2)}deg)`;
      sc.style.perspective = (lerp(BP,BP*.44,tO)+Math.sin(tO*Math.PI)*BP*.55).toFixed(0)+'px';
      sc.style.transform   = this._gfNudge();
    }

    _updateGFBack(p) {
      const u  = this.uid, k = `${u}-gb`;
      const P  = this._panelSize(), BP = P * 2.5;
      const sc = document.getElementById(`${k}-scene`);
      const wr = document.getElementById(`${k}-wrap`);
      const pL = document.getElementById(`${k}-pl`);
      const pR = document.getElementById(`${k}-pr`);
      const tO = phaseT(p, 0.20, 0.55);
      let lR=lerp(0,-166,tO), rR=lerp(0,-14,tO), wO=lerp(-P,-P/2,tO), persp=BP+Math.sin(tO*Math.PI)*BP*.55;
      const tC = phaseT(p, 0.60, 0.95);
      if (tC>0) lR=lerp(-166,-180,tC);
      if (tO>=1) rR=lerp(-14,-180,tC);
      if (tC>0) wO=lerp(-P/2,0,tC);
      if (tC>0||tO>=1) persp=BP+Math.sin(tC*Math.PI)*BP*.55;
      sc.style.perspective = persp.toFixed(0)+'px';
      sc.style.transform   = this._gfNudge();
      pL.style.transform   = `rotateY(${lR.toFixed(2)}deg)`;
      pR.style.transform   = `rotateY(${rR.toFixed(2)}deg)`;
      wr.style.left        = wO.toFixed(1)+'px';
      pR.style.zIndex      = tC>0?'3':'1';
      pL.style.zIndex      = tC>0?'1':'2';
      pL.style.opacity     = (1-clamp01((lR - -179) / -1)).toFixed(3);
    }

    _updateGFVinyl(p) {
      const u  = this.uid, k = `${u}-gv`;
      const P  = this._panelSize(), BP = P * 2.5;
      const sc = document.getElementById(`${k}-scene`);
      const wr = document.getElementById(`${k}-wrap`);
      const pL = document.getElementById(`${k}-pl`);
      const pR = document.getElementById(`${k}-pr`);
      const vL = document.getElementById(`${k}-vl`);
      const vR = document.getElementById(`${k}-vr`);
      const tO = phaseT(p, 0.20, 0.55);
      let lR=lerp(0,-166,tO), rR=lerp(0,-14,tO), wO=lerp(-P,-P/2,tO), persp=BP+Math.sin(tO*Math.PI)*BP*.55;
      const tC = phaseT(p, 0.60, 0.95);
      if (tC>0) lR=lerp(-166,-180,tC);
      if (tO>=1) rR=lerp(-14,-180,tC);
      if (tC>0) wO=lerp(-P/2,0,tC);
      if (tC>0||tO>=1) persp=BP+Math.sin(tC*Math.PI)*BP*.55;
      sc.style.perspective = persp.toFixed(0)+'px';
      sc.style.transform   = this._gfNudge();
      pL.style.transform   = `rotateY(${lR.toFixed(2)}deg)`;
      pR.style.transform   = `rotateY(${rR.toFixed(2)}deg)`;
      wr.style.left        = wO.toFixed(1)+'px';
      pR.style.zIndex      = tC>0?'3':'1';
      pL.style.zIndex      = tC>0?'1':'2';
      pL.style.opacity     = (1-clamp01((lR - -179) / -1)).toFixed(3);
      const tOut=phaseT(p,.55*.85,.55), tIn=phaseT(p,.60,.68);
      const dT=clamp01(tOut-tIn), dX=lerp(0,P*.44,dT), op=dT>0?'1':'0';
      vL.style.opacity=op; vL.style.transform=`translateX(${dX.toFixed(1)}px)`;
      vR.style.opacity=op; vR.style.transform=`translateX(${dX.toFixed(1)}px)`;
    }

    _updateExpand(p) {
      const u      = this.uid;
      const P      = this._panelSize();
      const peek   = this.cfg.expand.peek;
      const hasV2  = !!this.cfg.images.vinyl2;
      const cover  = document.getElementById(`${u}-ex-cover`);
      const gfL    = document.getElementById(`${u}-ex-gf`);
      const vinyl  = document.getElementById(`${u}-ex-vinyl`);
      const vinyl2 = hasV2 ? document.getElementById(`${u}-ex-vinyl2`) : null;

      const t    = phaseT(p, 0.00, 0.90);
      const STEP = P * peek;
      const GF   = P * 0.98;

      // At t=1: cover left edge = -STEP*0.5, rightmost right edge = furthest vinyl + GF.
      // Shift all layers by the difference between P/2 and the spread's midpoint.
      const rightEnd   = (hasV2 ? STEP * 2.6 : STEP * 2.2) + GF;
      const leftEnd    = -STEP * 0.5;
      const centX      = lerp(0, P / 2 - (leftEnd + rightEnd) / 2, t);

      // Gatefold
      gfL.style.opacity   = t > 0 ? '1' : '0';
      gfL.style.transform = `translateX(${(lerp(0, STEP * 0.8, t) + centX).toFixed(1)}px)`;

      // Vinyl 1
      const v1Rot = lerp(0, 160, t);
      vinyl.style.opacity   = t > 0 ? '1' : '0';
      vinyl.style.transform = `translateX(${(lerp(0, STEP * 2.2, t) + centX).toFixed(1)}px) rotate(${v1Rot.toFixed(1)}deg)`;

      // Vinyl 2
      if (hasV2 && vinyl2) {
        const v2Rot = lerp(0, 220, t);
        vinyl2.style.opacity   = t > 0 ? '1' : '0';
        vinyl2.style.transform = `translateX(${(lerp(0, STEP * 2.6, t) + centX).toFixed(1)}px) rotate(${v2Rot.toFixed(1)}deg)`;
      }

      cover.style.transform = `translateX(${(lerp(0, -STEP * 0.5, t) + centX).toFixed(1)}px)`;
    }

    _updateExpandTilt(p) {
      const u   = this.uid;
      const P   = this._panelSize(), BP = P * this.cfg.tilt.perspective;
      const el  = id => document.getElementById(`${u}-et-${id}`);
      const sc  = el('scene'), st = el('stack');
      const cvr = el('cover'), bk = el('back'), vR = el('vr'), vL = el('vl');
      const vR2 = el('vr2'),   vL2 = el('vl2');
      const gfR = el('gfr'),   gfL = el('gfl');
      const tiltStart = this.cfg.intro.enabled ? this.cfg.intro.tiltStart : null;
      let tT, scl;
      if (tiltStart != null) {
        // Tilt, lift and shadow run during the intro zoom, so the cover goes straight
        // from full screen to its tilted pose; skip that stretch of the main timeline.
        tT  = phaseT(this._introP, tiltStart, 1);
        scl = lerp(.72,1,tT);
        p   = lerp(.20,1,p);
      } else {
        tT  = phaseT(p,0,.20);
        scl = lerp(.72,1,phaseT(p,0,.25));
      }
      sc.style.perspective = lerp(P*22,BP,tT).toFixed(0)+'px';
      st.style.transform   =
        `scale(${scl.toFixed(4)}) `+
        `rotateX(${lerp(0,T_RX,tT).toFixed(2)}deg) `+
        `rotateY(${lerp(0,T_RY,tT).toFixed(2)}deg) `+
        `rotateZ(${lerp(0,T_RZ,tT).toFixed(2)}deg)`;
      bk.style.opacity = phaseT(p,.20,.35).toFixed(3);
      const M   = this.cfg.motion;
      const on  = M.enabled;
      // Each move: plain smoothstep, or (motion on) a settle with a per-piece delay
      const mv  = (a, b, d = 0) => on ? (p <= a+d ? 0 : settle(invlerp(a+d, b+d, p), M.overshoot)) : phaseT(p, a, b);
      const sg  = on ? M.stagger : 0;
      const tSc = mv(.35,.60), tSb = mv(.35,.60, sg);                 // cover leads, back follows
      const tVr = mv(.65,.88), tVl = mv(.65,.88, sg);                 // right vinyl leads
      const tGr = mv(.65,.88, sg*.5), tGl = mv(.65,.88, sg*1.5);      // gatefolds a beat behind
      const cx = P*(ET.split[0] + ET.nudge[0])*tSc, cy = P*(ET.split[1] + ET.nudge[1])*tSc; // cover position
      const bx = P*(ET.split[0] - ET.nudgeBack[0])*tSb, by = P*(ET.split[1] - ET.nudgeBack[1])*tSb; // back position (mirrored)
      // Cover lifts off the back as it slides, then settles back down
      const lift = on ? Math.sin(Math.PI*invlerp(.35,.60,p)) : 0;
      // Offsets are in table space; z index follows DOM order (vl, vr, vl2, vr2, gfl, back, gfr, cover)
      const L  = this.cfg.light;
      const lx = L.x*P, ly = L.y*P;
      // With the light on, shadows fall away from it, parallel across the set
      // (a large studio light, rather than a bare bulb that fans shadows out)
      const ld = Math.hypot(lx, ly) || 1;
      const [dx, dy] = L.enabled ? [-lx/ld, -ly/ld] : [-.54, .84];
      const shd = (k = 1) => (L.enabled || on) ? shadowDir(tT, dx, dy, k) : shadow(tT);
      const contact = (a, rot = 0) => {                               // tight shadow, in the layer's own (spun) frame
        const r = -rot*Math.PI/180, ox = dx*Math.cos(r) - dy*Math.sin(r), oy = dx*Math.sin(r) + dy*Math.cos(r);
        return `${(ox*4).toFixed(1)}px ${(oy*4).toFixed(1)}px 7px rgba(0,0,0,${(a*.4).toFixed(2)})`;
      };
      const place = (e, x, y, z, s, rot = 0, zLift = 0) => {
        e.style.transform = `translate3d(${x.toFixed(1)}px,${y.toFixed(1)}px,${(z*ET.zStep + zLift).toFixed(1)}px)` +
                            (s ? ` scale(${s})` : '') + (rot ? ` rotate(${rot.toFixed(1)}deg)` : '');
        if (L.enabled) this._lightLayer(e, x, y, s || 1, P, lx, ly, tT, rot);
      };
      // Vinyls stay hidden until they start sliding out; they turn as they go
      const spinR = on ? M.spin*tVr : 0, spinL = on ? -M.spin*tVl : 0;
      vL.style.opacity = tVl > 0 ? '1' : '0';
      vR.style.opacity = tVr > 0 ? '1' : '0';
      place(vL, -bx - P*ET.vinyl.left*tVl, -by, 0, .95, spinL);
      place(vR,  cx + P*ET.vinyl.right*tVr,  cy, 1, .95, spinR);
      const vlShadow = on && M.contact ? `drop-shadow(${contact(tVl > 0 ? 1 : 0, spinL)})` : '';
      const vrShadow = on && M.contact ? `drop-shadow(${contact(tVr > 0 ? 1 : 0, spinR)})` : '';
      // Inner vinyls: same slide, stopping part-way so they sit on top of the outer pair
      const inner = (v, x, y, z, t, spin) => {
        if (!v) return;
        v.style.opacity = t > 0 ? '1' : '0';
        place(v, x, y, z, .95, spin);
        v.firstChild.style.filter = on && M.contact ? `drop-shadow(${contact(t > 0 ? 1 : 0, spin)})` : '';
      };
      inner(vL2, -bx - P*ET.vinyl.left*ET.vinylInner.left*tVl, -by, 2, tVl, spinL*.6);
      inner(vR2,  cx + P*ET.vinyl.right*ET.vinylInner.right*tVr,  cy, 3, tVr, spinR*.6);
      // gfL: sits under back, slides out along −y
      if (gfL) {
        gfL.style.opacity = clamp01(tSb).toFixed(3);
        place(gfL, -bx, -by - P*ET.gfL*tGl, 4, .95);
        if (on && M.contact) gfL.style.boxShadow = contact(clamp01(tSb));
      }
      place(bk, -bx, -by, 5);
      bk.style.boxShadow = shd();
      // gfR: sits under cover, slides out along +y
      if (gfR) {
        gfR.style.opacity = clamp01(tSc).toFixed(3);
        place(gfR,  cx,  cy + P*ET.gfR*tGr, 6, .95);
        if (on && M.contact) gfR.style.boxShadow = contact(clamp01(tSc));
      }
      place(cvr, cx, cy, 7, 0, 0, lift*M.lift*P);
      cvr.style.boxShadow = shd(1 + lift*1.5);
      if (L.enabled) this._glint(cvr, tT);

      // ── Camera ──
      const C = this.cfg.camera;
      let blurCover = 0, blurSleeves = 0;
      if (C.enabled) {
        // Push in (and ease the view up-left a touch) as the set assembles
        const tP = phaseT(p,.45,1);
        sc.style.transform         = `scale(${(1 + C.push*tP).toFixed(4)})`;
        sc.style.perspectiveOrigin = `${(50 - 4*tP).toFixed(2)}% ${(40 - 3*tP).toFixed(2)}%`;
        el('cam').style.setProperty('--d', tT.toFixed(3));
        const vg = document.getElementById(`${u}-vignette`);
        if (vg) vg.style.opacity = (C.vignette*tT).toFixed(3);
        // Rack focus: cover is the hero during the split (supporting pieces soft),
        // focus pulls to the vinyl as it slides out (sleeves soft), then all sharp for the end frame
        const bell = (a, b) => Math.sin(Math.PI*invlerp(a, b, p));
        const onVinyl = bell(.64,.97);
        blurSleeves = C.focus * Math.max(bell(.26,.64), onVinyl);
        blurCover   = C.focus * onVinyl;
      }
      const blur = px => px > .05 ? `blur(${px.toFixed(2)}px)` : '';
      cvr.firstChild.style.filter = blur(blurCover);
      bk.firstChild.style.filter  = blur(blurSleeves);
      if (gfL) gfL.firstChild.style.filter = blur(blurSleeves);
      if (gfR) gfR.firstChild.style.filter = blur(blurSleeves);
      vL.firstChild.style.filter = vlShadow;
      vR.firstChild.style.filter = vrShadow;
    }

    // One key light, fixed on the table: each layer's highlight is placed relative to
    // where that layer sits, so the light stays put as pieces slide beneath it.
    // Strength follows the tilt, so the full-screen cover keeps its true colours.
    _lightLayer(e, x, y, s, P, lx, ly, tT, rot = 0) {
      const u = this.uid, L = this.cfg.light;
      const hi = e.querySelector(`.${u}-et-light`), sd = e.querySelector(`.${u}-et-shade`);
      if (hi) {
        const at = `circle ${(L.radius*P/s).toFixed(0)}px at `+
                   `${(50 + (lx - x)/s/P*100).toFixed(1)}% ${(50 + (ly - y)/s/P*100).toFixed(1)}%`;
        const op = (L.intensity*tT).toFixed(3);
        hi.style.opacity    = sd.style.opacity = op;
        hi.style.background = `radial-gradient(${at}, rgba(255,255,255,.55), rgba(255,255,255,0) 60%)`;
        sd.style.background = `radial-gradient(${at}, rgba(0,0,0,0) 45%, rgba(0,0,0,.35))`;
      }
      const sh = e.querySelector(`.${u}-et-sheen`);
      if (sh) {
        // Point the groove streak (centred at 32° in the gradient) at the light
        // (minus the layer's own spin, so the streak stays put while the record turns)
        const a = Math.atan2(lx - x, -(ly - y)) * 180/Math.PI - 32 - rot;
        sh.style.opacity   = (L.intensity*tT).toFixed(3);
        sh.style.transform = `rotate(${a.toFixed(1)}deg)`;
      }
    }

    // Scroll-driven glint: as the cover tilts away from the viewer, the light's
    // reflection sweeps across it from the lit corner to the far one (and back on scroll-up)
    _glint(cvr, tT) {
      const g = cvr.querySelector(`.${this.uid}-et-glint`);
      if (!g) return;
      const t = invlerp(.15, .95, tT);
      g.style.opacity            = (Math.sin(t*Math.PI) * this.cfg.light.glintStrength).toFixed(3);
      g.style.backgroundPosition = `${lerp(100, 0, t).toFixed(1)}% 0`;
    }

    // ── Pointer tracking + rAF loop ───────────────────────────
    _pointerLoop() {
      if (!this.cfg.pointer.enabled || !this._stage.isConnected) return;
      const dx = this.ptr.tx - this.ptr.x;
      const dy = this.ptr.ty - this.ptr.y;
      if (Math.abs(dx) > 0.0001 || Math.abs(dy) > 0.0001) {
        this.ptr.x += dx * this.cfg.pointer.ease;
        this.ptr.y += dy * this.cfg.pointer.ease;
        this._update();
      }
      requestAnimationFrame(() => this._pointerLoop());
    }

    // ── Event binding ─────────────────────────────────────────
    _bindEvents() {
      const move = (cx, cy) => {
        if (!this.cfg.pointer.enabled) return;
        this.ptr.tx = ((cx / window.innerWidth)  - 0.5) * 2;
        this.ptr.ty = ((cy / window.innerHeight) - 0.5) * 2;
      };
      window.addEventListener('mousemove', e => move(e.clientX, e.clientY), { passive: true });
      window.addEventListener('touchmove', e => { if (e.touches.length) move(e.touches[0].clientX, e.touches[0].clientY); }, { passive: true });
      window.addEventListener('touchend',  () => { if (this.cfg.pointer.enabled) { this.ptr.tx=0; this.ptr.ty=0; } }, { passive: true });
      window.addEventListener('resize', () => {
        const w = window.innerWidth;
        if (Math.abs(w - this._stableW) < 5) return; // height-only = mobile chrome toggle, ignore
        this._stableW  = w;
        this._stableVH = window.innerHeight;
        this._applyGatefoldSizing();
        this._update();
      }, { passive: true });
      window.addEventListener('scroll', () => {
        if (!this._section.isConnected) return;
        if (!this._scrollTick) {
          this._scrollTick = true;
          requestAnimationFrame(() => { this._update(); this._scrollTick = false; });
        }
      }, { passive: true });
    }
  }

  // ── Public API ────────────────────────────────────────────────
  return {
    /**
     * Create a new viewer instance.
     * @param {string|Element} target  CSS selector or DOM element to inject into
     * @param {object}         config  Optional config (images, modes, panel, pointer)
     * @returns {Instance}
     */
    init(target, config = {}) {
      const container = typeof target === 'string' ? document.querySelector(target) : target;
      if (!container) { console.error('ArtworkViewer: target not found', target); return null; }
      return new Instance(container, config);
    }
  };

})();
